import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import reportWebVitals from './reportWebVitals';
import { APP_VERSION } from './version';
import { ensureSchemas } from './utils/storageSchema';
import { I18nProvider } from './i18n';
import './perfVitals';
import './sentryInit';

// Expose version in console (one-liner, subdued)
// eslint-disable-next-line no-console
console.info(`[Yatzy] Version ${APP_VERSION}`);
try { ensureSchemas(); } catch {}

// Register service worker for PWA
// Endast i produktion: build/service-worker.js genereras av workbox vid build.
// I dev finns ingen SW (och en cachad produktions-SW skulle förstöra hot reload).
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then(reg => {
        // If there's an updated SW waiting, notify app immediately
        if (reg.waiting) {
          console.log('[SW] Update waiting, notifying app');
          const ev = new CustomEvent('app-sw-update', { detail: { registration: reg } });
          window.dispatchEvent(ev);
        }

        // When a new SW is installed and waiting, notify app
        reg.addEventListener('updatefound', () => {
          const sw = reg.installing;
          if (!sw) return;
          console.log('[SW] New SW installing');
          sw.addEventListener('statechange', () => {
            console.log('[SW] SW state changed to:', sw.state);
            if (sw.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[SW] New SW installed, notifying app');
              const ev = new CustomEvent('app-sw-update', { detail: { registration: reg } });
              window.dispatchEvent(ev);
            }
          });
        });

        // Periodically check for updates while app is open (more frequent)
        const interval = setInterval(() => {
          console.log('[SW] Checking for updates...');
          reg.update().catch(() => {});
        }, 30 * 60 * 1000); // every 30 minutes instead of hourly
        window.addEventListener('beforeunload', () => clearInterval(interval));

        // Also check for updates on page focus
        window.addEventListener('focus', () => {
          console.log('[SW] Page focused, checking for updates...');
          reg.update().catch(() => {});
        });
      })
      .catch(err => {
        console.error('SW registration failed', err);
      });
  });
}

export async function requestNotificationPermission() {
  if (!('Notification' in window)) return false;
  const perm = await Notification.requestPermission();
  return perm === 'granted';
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <I18nProvider>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </I18nProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
